import { useContext, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useHistory, useLocation } from "react-router-dom";
import { MdSearch } from "react-icons/md";
import { useAccountData } from "../../../../Hooks";
import { useAuth } from "../../../../Contexts/AuthContext";
import { UserContext } from "../../../../Contexts/User";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useToast } from "../../../../Hooks/toast";
import { BaasInstancesSkeleton } from "../components/BaasSkeleton";
import { BaasCreateInstanceCard } from "./components/BaasCreateInstanceCard";
import { BaasEditInstanceModal } from "./components/BaasEditInstanceModal";
import { BaasInstanceCard } from "./components/BaasInstanceCard";
import { isValutSegregationWalletInstance } from "../../../../Services/baasAccountsListApi";
import {
  groupBaasInstancesByProgram,
  isBaasKybPending,
  isBaasProdAwaitingLiberation,
  programGroupMatchesEnvFilter,
} from "../../../../Services/baasInstancesApi";
import { BAAS_INSTANCE_CREATE_PATH } from "../constants";

const ENV_FILTER_ALL = "all";
const ENV_FILTER_HML = "hml";
const ENV_FILTER_PRODUCTION = "production";

const resolveDisplayName = (...candidates) => {
  const resolved = candidates.find((value) => typeof value === "string" && value.trim());
  if (!resolved) {
    return "";
  }

  return resolved.trim().split(/\s+/)[0].toUpperCase();
};

const matchesSearch = (group, query) => {
  const normalized = String(query || "")
    .trim()
    .toLowerCase();
  if (!normalized) return true;

  const members = [group?.production, group?.development].filter(Boolean);
  const haystack = [
    group?.name,
    group?.programId,
    ...members.flatMap((instance) => [
      instance?.name,
      instance?.displayName,
      instance?.programId,
      instance?.environment,
      instance?.environmentDisplayName,
      instance?.baasInstanceId,
      instance?.id,
    ]),
  ]
    .filter((value) => value != null && value !== "")
    .map((value) => String(value).toLowerCase())
    .join(" ");

  return haystack.includes(normalized);
};

/**
 * Administração section — only the true Valut global-admin program (VALUT-GLOBAL /
 * AllowGlobalAccess). Client programs named VALUT-* (e.g. VALUT-SAUDE) stay under
 * Instâncias de clientes.
 */
const isValutAdminProgramGroup = (group) => {
  if (!group) return false;
  if (group.allowGlobalAccess === true) return true;
  return [group, group.production, group.development]
    .filter(Boolean)
    .some((row) => isValutSegregationWalletInstance(row));
};

const shouldRedirectToCreatePage = (search) => {
  const params = new URLSearchParams(search || "");
  const create = String(params.get("create") || params.get("nova") || "").toLowerCase();
  return create === "1" || create === "true" || create === "yes";
};

const ProgramCardsGrid = ({
  groups,
  envFilter,
  onSelect,
  onEdit,
  onCreateHml,
  instances,
  canOpenAwaitingProd,
}) => (
  <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {groups.map((group) => (
      <BaasInstanceCard
        key={group.id}
        group={group}
        preferredEnv={envFilter}
        onSelect={onSelect}
        instances={instances}
        canOpenAwaitingProd={canOpenAwaitingProd}
        onEdit={
          group.production?.baasInstanceId || group.development?.baasInstanceId
            ? undefined
            : onEdit
        }
        onCreateHml={onCreateHml}
      />
    ))}
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
    {children}
  </p>
);

export const BaasInstancesPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [userContext] = useContext(UserContext);
  const { state: accountData, handleGET: fetchAccountGET, loading: accountLoading } = useAccountData();
  const {
    instances,
    selectInstance,
    updateInstance,
    loading: instancesLoading,
    clearSelectedInstance,
    error: instancesError,
  } = useBaasInstance();
  const {
    canCreateInstance,
    isValutConfigAdmin,
    canUseViewAs,
    loading: createAccessLoading,
  } = useBaasPlatformCreateAccess();
  const pageLoading = useBaasPageLoading("instances");
  const isLoading =
    pageLoading || accountLoading.GET || instancesLoading || createAccessLoading;
  const [editingInstance, setEditingInstance] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [envFilter, setEnvFilter] = useState(ENV_FILTER_ALL);
  const [searchQuery, setSearchQuery] = useState("");
  /** Raw Valut matrix — may open Prod cards awaiting liberação (ops / Aprovar produção). */
  const canOpenAwaitingProd = Boolean(canUseViewAs);

  useEffect(() => {
    fetchAccountGET();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Home /baas is the env picker — do not keep a previous Production selection sticky.
  useEffect(() => {
    clearSelectedInstance();
  }, [clearSelectedInstance]);

  const displayName = useMemo(
    () =>
      resolveDisplayName(
        accountData?.userName,
        userContext?.userName,
        user?.username?.split("@")?.[0],
      ),
    [accountData?.userName, user?.username, userContext?.userName],
  );

  const programGroups = useMemo(
    () => groupBaasInstancesByProgram(instances),
    [instances],
  );

  const filteredGroups = useMemo(
    () =>
      programGroups.filter(
        (group) =>
          programGroupMatchesEnvFilter(group, envFilter) && matchesSearch(group, searchQuery),
      ),
    [programGroups, envFilter, searchQuery],
  );

  /**
   * Valut Administração section only for matrix / super_user.
   * Client multi-instance creators see a flat list of their OwnerEntityId programs
   * (API already membership-scoped) plus Criar instância — never Valut matrix cards.
   */
  const { adminGroups, clientGroups } = useMemo(() => {
    if (!isValutConfigAdmin) {
      return { adminGroups: [], clientGroups: filteredGroups };
    }
    const admin = [];
    const clients = [];
    filteredGroups.forEach((group) => {
      if (isValutAdminProgramGroup(group)) {
        admin.push(group);
      } else {
        clients.push(group);
      }
    });
    return { adminGroups: admin, clientGroups: clients };
  }, [filteredGroups, isValutConfigAdmin]);

  const showPlatformAdminUi = isValutConfigAdmin;

  // Legacy `?create=1` / `?nova=1` → full create page (platform create only).
  useEffect(() => {
    if (shouldRedirectToCreatePage(location.search) && canCreateInstance) {
      history.replace(BAAS_INSTANCE_CREATE_PATH);
    }
  }, [history, location.search, canCreateInstance]);

  const handleOpenCreate = () => {
    if (!canCreateInstance) return;
    history.push(BAAS_INSTANCE_CREATE_PATH);
  };

  const handleSelectInstance = (instance) => {
    // Partner/instance admins: Prod awaiting liberação stays closed — use HML instead.
    if (isBaasProdAwaitingLiberation(instance) && !canOpenAwaitingProd) {
      return;
    }
    // Card id is `{baasInstanceId}:{environment}` — Development → Dev DB, Production → Prod DB.
    selectInstance(instance.id);
    history.push(isBaasKybPending(instance) ? "/baas/kyb" : "/baas/inicio");
  };

  const handleCreateHml = async (productionInstance) => {
    if (!productionInstance?.baasInstanceId) return;
    try {
      // Cross-DB only: MS intercepts create_hml_twin and creates twin in bank (never same-DB).
      await updateInstance(productionInstance.id, { createHmlTwin: true });
      addToast({
        type: "success",
        title: intl.get("baas.instances.createHmlCard.success"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: intl.get("baas.instances.createHmlCard.errorTitle"),
        description:
          error?.detail ||
          error?.message ||
          intl.get("baas.instances.createHmlCard.error"),
      });
      throw error;
    }
  };

  const handleEditInstance = async (payload) => {
    if (!editingInstance) {
      return;
    }

    setUpdating(true);
    try {
      await updateInstance(editingInstance.id, payload);
      setEditingInstance(null);
    } catch (error) {
      addToast({
        type: "error",
        title: intl.get("baas.instances.editModal.apiReadOnlyTitle"),
        description:
          error?.detail ||
          error?.message ||
          intl.get("baas.instances.editModal.apiReadOnly"),
      });
    } finally {
      setUpdating(false);
    }
  };

  if (isLoading) {
    return <BaasInstancesSkeleton count={Math.max(programGroups.length, 2)} />;
  }

  const filterChips = [
    { id: ENV_FILTER_ALL, label: intl.get("baas.instances.filter.all") },
    { id: ENV_FILTER_HML, label: intl.get("baas.instances.filter.hml") },
    { id: ENV_FILTER_PRODUCTION, label: intl.get("baas.instances.filter.production") },
  ];

  const showClientEmpty =
    !showPlatformAdminUi &&
    !instancesError &&
    programGroups.length > 0 &&
    filteredGroups.length === 0;

  const editHandler = canCreateInstance ? setEditingInstance : undefined;

  return (
    <div className="baas-instances min-w-0">
      <header className="mb-6 min-w-0">
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100 md:text-3xl">
          {displayName
            ? intl.get("baas.instances.welcome", { name: displayName })
            : intl.get("baas.instances.welcomeGeneric")}
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.instances.subtitleEnv")}
        </p>
      </header>

      <div className="mb-6 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {intl.get("baas.instances.filter.label")}
          </p>
          <div
            className="inline-flex w-fit flex-wrap gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-brand-dark-border dark:bg-brand-dark-elevated"
            role="group"
            aria-label={intl.get("baas.instances.filter.ariaLabel")}
          >
            {filterChips.map((chip) => {
              const selected = envFilter === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setEnvFilter(chip.id)}
                  aria-pressed={selected}
                  className={
                    selected
                      ? "rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-brand-dark shadow-sm dark:bg-brand-dark-surface dark:text-gray-100"
                      : "rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-100"
                  }
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        <label className="relative block w-full min-w-0 sm:max-w-xs">
          <span className="sr-only">{intl.get("baas.instances.search.ariaLabel")}</span>
          <MdSearch
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={intl.get("baas.instances.search.placeholder")}
            className="h-10 w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
          />
        </label>
      </div>

      {showPlatformAdminUi ? (
        <>
          <section className="min-w-0" aria-labelledby="baas-instances-admin-heading">
            <SectionLabel>
              <span id="baas-instances-admin-heading">
                {intl.get("baas.instances.section.admin")}
              </span>
            </SectionLabel>
            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <BaasCreateInstanceCard onClick={handleOpenCreate} />
              {adminGroups.map((group) => (
                <BaasInstanceCard
                  key={group.id}
                  group={group}
                  preferredEnv={envFilter}
                  instances={instances}
                  onSelect={handleSelectInstance}
                  canOpenAwaitingProd={canOpenAwaitingProd}
                  onCreateHml={isValutConfigAdmin ? handleCreateHml : undefined}
                  onEdit={
                    group.production?.baasInstanceId || group.development?.baasInstanceId
                      ? undefined
                      : editHandler
                  }
                />
              ))}
            </div>
          </section>

          <div
            className="my-8 border-t border-gray-200 dark:border-brand-dark-border"
            role="separator"
            aria-hidden
          />

          <section className="min-w-0" aria-labelledby="baas-instances-clients-heading">
            <SectionLabel>
              <span id="baas-instances-clients-heading">
                {intl.get("baas.instances.section.clients")}
              </span>
            </SectionLabel>
            {clientGroups.length > 0 ? (
              <ProgramCardsGrid
                groups={clientGroups}
                envFilter={envFilter}
                instances={instances}
                onSelect={handleSelectInstance}
                onEdit={editHandler}
                onCreateHml={isValutConfigAdmin ? handleCreateHml : undefined}
                canOpenAwaitingProd={canOpenAwaitingProd}
              />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("baas.instances.section.clientsEmpty")}
              </p>
            )}
          </section>
        </>
      ) : (
        <>
          {canCreateInstance ? (
            <div className="mb-4 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <BaasCreateInstanceCard onClick={handleOpenCreate} />
            </div>
          ) : null}
          <ProgramCardsGrid
            groups={clientGroups}
            envFilter={envFilter}
            instances={instances}
            onSelect={handleSelectInstance}
            onEdit={undefined}
            onCreateHml={isValutConfigAdmin ? handleCreateHml : undefined}
            canOpenAwaitingProd={canOpenAwaitingProd}
          />
        </>
      )}

      {instancesError ? (
        <p className="mt-6 text-sm text-red-600 dark:text-red-400">
          {instancesError?.detail ||
            instancesError?.message ||
            intl.get("baas.instances.loadError")}
        </p>
      ) : null}

      {!instancesError && programGroups.length === 0 ? (
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.instances.loadEmpty")}
        </p>
      ) : null}

      {showClientEmpty ? (
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.instances.filter.empty")}
        </p>
      ) : null}

      <BaasEditInstanceModal
        open={Boolean(editingInstance)}
        instance={editingInstance}
        onClose={() => setEditingInstance(null)}
        onSubmit={handleEditInstance}
        submitting={updating}
        canEditCatalogFlags={
          Boolean(editingInstance?.baasInstanceId) && Boolean(isValutConfigAdmin)
        }
      />
    </div>
  );
};
